/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */


interface Tab {
  value: string;
  label: string;
}

const styles = css`
  :host {
    display: block;
  }

  .root { display: flex; flex-direction: column; gap: 8px; }
  .root.vertical { align-items: flex-start; flex-direction: row; }

  .list {
    align-items: center;
    background: var(--muted, #f4f4f5);
    border-radius: var(--radius-lg, 10px);
    display: inline-flex;
    gap: 4px;
    padding: 3px;
    min-height: 36px;
  }

  .trigger {
    background: transparent;
    border: 0;
    border-radius: 7px;
    color: var(--muted-foreground, #71717a);
    cursor: pointer;
    font: inherit;
    font-size: 0.875rem;
    font-weight: 500;
    outline: none;
    padding: 5px 12px;
    transition:
      background-color 140ms ease,
      color 140ms ease,
      box-shadow 140ms ease;
  }

  .trigger:focus-visible {
    box-shadow: 0 0 0 3px var(--ring-shadow, rgb(24 24 27 / 12%));
  }

  .trigger[aria-selected="true"] {
    background: var(--background, #ffffff);
    box-shadow: 0 1px 2px rgb(0 0 0 / 8%);
    color: var(--foreground, #18181b);
  }

  .panels {
    flex: 1;
  }

  .root.vertical .list { align-items: stretch; flex-direction: column; height: auto; }
  .root.vertical .trigger { text-align: left; }
  .root.line { gap: 0; }
  .root.line .list { background: transparent; border-radius: 0; gap: 0; height: 48px; min-height: 48px; padding: 0; }
  .root.line .trigger { align-items: center; align-self: stretch; border-radius: 0; display: inline-flex; justify-content: center; line-height: 1; padding: 0 12px; position: relative; }
  .root.line .trigger[aria-selected="true"] { background: transparent; box-shadow: none; }
  .root.line .trigger[aria-selected="true"]::after { background: var(--foreground); bottom: 0; content: ""; height: 2px; inset-inline: 12px; position: absolute; }
  .root.vertical.line .trigger[aria-selected="true"]::after { bottom: 0; height: auto; inset-block: 0; left: auto; right: -5px; width: 2px; }
`;

export class AppTabs extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppTabs)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["tabs","data-tabs","value","data-value","variant","data-variant","orientation","data-orientation"];

  #tabs = [];
  #value = "";
  #variant = "default";
  #orientation = "horizontal";
  #tabsSet = false;
  #valueSet = false;
  #variantSet = false;
  #orientationSet = false;
  #textStart0;
  #textEnd0;
  #textStart1;
  #textEnd1;
  #attr0;
  #attr1;
  #attrLast0;
  #attrLast1;
  #lists = {};
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get tabs() {
    return this.#tabs;
  }

  set tabs(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#tabs, next)) return;
    this.#tabs = next;
    this.#tabsSet = true;
    this.#updateBindings('tabs');
  }

  get value() {
    return this.#value;
  }

  set value(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#value, next)) return;
    this.#value = next;
    this.#valueSet = true;
    this.#updateBindings('value');
  }

  get variant() {
    return this.#variant;
  }

  set variant(value) {
    const raw = value ?? "default";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#variant, next)) return;
    this.#variant = next;
    this.#variantSet = true;
    this.#updateBindings('variant');
  }

  get orientation() {
    return this.#orientation;
  }

  set orientation(value) {
    const raw = value ?? "horizontal";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#orientation, next)) return;
    this.#orientation = next;
    this.#orientationSet = true;
    this.#updateBindings('orientation');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-tabs')) this.tabs = this.dataset.tabs;
    if (this.hasAttribute('data-value')) this.value = this.dataset.value;
    if (this.hasAttribute('data-variant')) this.variant = this.dataset.variant;
    if (this.hasAttribute('data-orientation')) this.orientation = this.dataset.orientation;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.tabs = props.tabs;
      this.value = props.value;
      this.variant = props.variant;
      this.orientation = props.orientation;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#tabsSet) this.tabs = this.dataset.tabs;
    if (!this.#valueSet) this.value = this.dataset.value;
    if (!this.#variantSet) this.variant = this.dataset.variant;
    if (!this.#orientationSet) this.orientation = this.dataset.orientation;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'tabs':
      case 'data-tabs':
        this.tabs = newValue;
        break;
      case 'value':
      case 'data-value':
        this.value = newValue;
        break;
      case 'variant':
      case 'data-variant':
        this.variant = newValue;
        break;
      case 'orientation':
      case 'data-orientation':
        this.orientation = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    const active = this.#value || (this.#tabs[0]?.value ?? "");
    return `<style>${this.#escText(styles)}</style><div class="${this.#escAttr(`root ${this.#variant} ${this.#orientation}`)}" data-argon-bind="0"><div class="list" role="tablist" aria-orientation="${this.#escAttr(this.#orientation)}" data-argon="2618" data-argon-bind="1"><!--argon-text:0-->${this.#tabs.map((tab) => `<button class="trigger" type="button" role="tab" data-value="${this.#escAttr(tab.value)}" aria-selected="${this.#escAttr((active === tab.value) ? "true" : "false")}">${this.#escText(tab.label)}</button>`).join("")}<!--/argon-text:0--></div><div class="panels"><!--argon-text:1-->${this.#tabs.map((tab) => `<slot name="${this.#escAttr(tab.value)}" style="${this.#escAttr((active === tab.value) ? "" : "display:none")}"></slot>`).join("")}<!--/argon-text:1--></div></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #adoptList(start, end, htmls) {
    const specs = [];
    let node = start.nextSibling;
    for (const html of htmls) {
      const template = document.createElement('template');
      template.innerHTML = html;
      const nodes = [];
      const anchor = node;
      for (const fresh of template.content.childNodes) {
        if (!node || node === end || node.nodeType !== fresh.nodeType || (node.nodeType === 1 && node.localName !== fresh.localName)) return;
        nodes.push(node);
        node = node.nextSibling;
      }
      specs.push({ anchor, nodes });
    }
    if (node !== end) return;
    return specs.map(({ anchor, nodes }) => {
      const mark = document.createComment('argon-item');
      (anchor ?? end).before(mark);
      return [mark, ...nodes];
    });
  }

  #reconcileList(id, start, end, items) {
    if (!start || !end) return;
    let list = this.#lists[id];
    if (!list) {
      const htmls = items.map(String);
      const nodes = this.#hydrating ? this.#adoptList(start, end, htmls) : undefined;
      if (nodes) list = this.#lists[id] = { htmls, marks: nodes.map(nodes => nodes[0]) };
      else {
        let node = start.nextSibling;
        while (node && node !== end) { const next = node.nextSibling; node.remove(); node = next; }
        list = this.#lists[id] = { htmls: [], marks: [] };
      }
    }
    const { htmls, marks } = list;
    if (items.length < htmls.length) {
      let node = marks[items.length];
      while (node && node !== end) { const next = node.nextSibling; node.remove(); node = next; }
      htmls.length = items.length;
      marks.length = items.length;
    }
    for (let i = 0; i < items.length; i++) {
      const html = String(items[i]);
      if (i < htmls.length) {
        const stop = i + 1 < marks.length ? marks[i + 1] : end;
        if (htmls[i] === html) {
          continue;
        }
        let node = marks[i].nextSibling;
        while (node && node !== stop) { const next = node.nextSibling; node.remove(); node = next; }
        const template = document.createElement('template');
        template.innerHTML = html;
        this.#wireEvents(template.content);
        stop.before(template.content);
        htmls[i] = html;
      } else {
        const mark = document.createComment('argon-item');
        const template = document.createElement('template');
        template.innerHTML = html;
        this.#wireEvents(template.content);
        end.before(mark, template.content);
        marks.push(mark);
        htmls.push(html);
      }
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const active = (prop === undefined || prop === 'tabs' || prop === 'value') ? (this.#value || (this.#tabs[0]?.value ?? "")) : undefined;
    if (prop === undefined || prop === 'tabs' || prop === 'value') {
      this.#reconcileList(0, this.#textStart0, this.#textEnd0, this.#tabs.map((tab) => `<button class="trigger" type="button" role="tab" data-value="${this.#escAttr(tab.value)}" aria-selected="${this.#escAttr((active === tab.value) ? "true" : "false")}">${this.#escText(tab.label)}</button>`));
    }
    if (prop === undefined || prop === 'tabs' || prop === 'value') {
      this.#reconcileList(1, this.#textStart1, this.#textEnd1, this.#tabs.map((tab) => `<slot name="${this.#escAttr(tab.value)}" style="${this.#escAttr((active === tab.value) ? "" : "display:none")}"></slot>`));
    }
    if ((prop === undefined || prop === 'variant' || prop === 'orientation') && this.#attr0) {
      const __argonNext = `${`root ${this.#variant} ${this.#orientation}`}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('class', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'orientation') && this.#attr1) {
      const __argonNext = `${this.#orientation}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr1.setAttribute('aria-orientation', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '2618').forEach(el => this.#listen(el, '2618', 'click', (event: Event) => {
    const trigger = (event.target as Element).closest(".trigger");
    if (!trigger)
        return;
    const next = trigger.getAttribute("data-value") ?? "";
    const current = this.#value || (this.#tabs[0]?.value ?? "");
    if (current === next)
        return;
    this.dispatchEvent(new CustomEvent("tab-change", { bubbles: true, composed: true, detail: { value: next } }));
}));
    this.#eventTargets(scope, '2618').forEach(el => this.#listen(el, '2618', 'keydown', (event: KeyboardEvent) => {
    const horizontal = this.#orientation === "horizontal";
    const previous = event.key === (horizontal ? "ArrowLeft" : "ArrowUp");
    const nextKey = event.key === (horizontal ? "ArrowRight" : "ArrowDown");
    if (!previous && !nextKey && event.key !== "Home" && event.key !== "End")
        return;
    event.preventDefault();
    const index = Math.max(0, this.#tabs.findIndex((tab) => tab.value === active));
    const nextIndex = event.key === "Home" ? 0 : event.key === "End" ? this.#tabs.length - 1 : previous ? (index - 1 + this.#tabs.length) % this.#tabs.length : (index + 1) % this.#tabs.length;
    const next = this.#tabs[nextIndex]?.value ?? active;
    this.dispatchEvent(new CustomEvent("tab-change", { bubbles: true, composed: true, detail: { value: next } }));
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
